import { useEffect, useRef, useState } from 'react';
import { generateSecurePassword } from '../../../shared/lib/generatePassword';

// 04-enforce.md 4.1 -- extraído de ClientsView.jsx. Responsabilidad única: el wizard inline de
// "agregar cliente" de la tabla de terminales (4 pasos: industria → cliente → transacción →
// operador, con autocompletado por teclado en 3 de ellos). Era ~250 líneas operativas de estado
// + handlers entremezclados con el resto de ClientsView. El JSX sigue en `AddClientWizardRow`;
// este hook le entrega todos los props.

const emptyWizardData = () => ({
  product: 'terminales',
  operation_date: new Date().toISOString().split('T')[0],
  status: 'PROSPECTO',
  client_id: null,
  cliente_input: '',
  industry: '',
  industria_input: '',
  transaction: '',
  oper_id: null,
  oper_input: '',
});

// Navegación circular de una lista de sugerencias con las flechas.
const cycle = (setIdx, len, dir) => {
  if (len > 0) setIdx(prev => (prev + dir + len) % len);
};

export function useAddClientWizard({ allUsers, ops, user, spacePrefix, createUserMutation, createOpMutation, createNotificationMutation, toast, translate }) {
  const [wizardActive, setWizardActive] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0: industria, 1: cliente, 1.5: crear cliente, 2: transacción, 3: operador
  const [wizardData, setWizardData] = useState(emptyWizardData);

  const [clientMatches, setClientMatches] = useState([]);
  const [clientActiveIdx, setClientActiveIdx] = useState(0);
  const [industryMatches, setIndustryMatches] = useState([]);
  const [industryActiveIdx, setIndustryActiveIdx] = useState(0);
  const [operMatches, setOperMatches] = useState([]);
  const [operActiveIdx, setOperActiveIdx] = useState(0);

  const clientInputRef = useRef(null);
  const industryInputRef = useRef(null);
  const transInputRef = useRef(null);
  const operInputRef = useRef(null);

  useEffect(() => {
    if (!wizardActive) return;
    if (wizardStep === 1 && clientInputRef.current) clientInputRef.current.focus();
    else if (wizardStep === 0 && industryInputRef.current) industryInputRef.current.focus();
    else if (wizardStep === 2 && transInputRef.current) transInputRef.current.focus();
    else if (wizardStep === 3 && operInputRef.current) operInputRef.current.focus();
  }, [wizardActive, wizardStep]);

  const resetMatches = () => {
    setClientMatches([]); setClientActiveIdx(0);
    setIndustryMatches([]); setIndustryActiveIdx(0);
    setOperMatches([]); setOperActiveIdx(0);
  };

  const startWizard = () => {
    setWizardActive(true);
    setWizardStep(0);
    setWizardData(emptyWizardData());
    resetMatches();
  };
  const cancelWizard = () => setWizardActive(false);

  const matchUsersByNick = (val) =>
    allUsers.filter(u => (u.nick || '').toLowerCase().includes(val.toLowerCase())).slice(0, 5);

  // ── Step 0: industria ──────────────────────────────────────────────────
  const handleIndustryInputChange = (val) => {
    setWizardData(prev => ({ ...prev, industria_input: val }));
    if (!val.trim()) { setIndustryMatches([]); setIndustryActiveIdx(0); return; }
    const existing = Array.from(new Set(ops.map(o => o.industry).filter(Boolean)));
    setIndustryMatches(existing.filter(o => o.toLowerCase().includes(val.toLowerCase())).slice(0, 7));
    setIndustryActiveIdx(0);
  };
  const selectIndustry = (tagVal) => {
    setWizardData(prev => ({ ...prev, industry: tagVal, industria_input: tagVal }));
    setIndustryMatches([]);
    setWizardStep(1);
  };
  const handleIndustryKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); cycle(setIndustryActiveIdx, industryMatches.length, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cycle(setIndustryActiveIdx, industryMatches.length, -1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      selectIndustry(industryMatches.length > 0 ? industryMatches[industryActiveIdx] : wizardData.industria_input.trim());
    }
  };

  // ── Step 1: cliente ────────────────────────────────────────────────────
  const handleClientInputChange = (val) => {
    setWizardData(prev => ({ ...prev, cliente_input: val }));
    if (!val.trim()) { setClientMatches([]); setClientActiveIdx(0); return; }
    setClientMatches(matchUsersByNick(val));
    setClientActiveIdx(0);
  };
  const selectClient = (selectedUser) => {
    setWizardData(prev => ({ ...prev, client_id: selectedUser.id, cliente_input: selectedUser.nick }));
    setClientMatches([]);
    setWizardStep(2);
  };
  // Compartida entre el atajo de teclado (Enter sin matches) y el link "Agregar cliente".
  const startCreateClient = () => {
    setWizardData(p => ({ ...p, new_client_name: p.cliente_input, new_client_email: '', new_client_password: generateSecurePassword() }));
    setWizardStep(1.5);
  };
  const handleClientKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); cycle(setClientActiveIdx, clientMatches.length, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cycle(setClientActiveIdx, clientMatches.length, -1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (clientMatches.length > 0) selectClient(clientMatches[clientActiveIdx]);
      else if (wizardData.cliente_input.trim()) startCreateClient();
    }
  };
  const handleCreateClient = async () => {
    try {
      if (!wizardData.new_client_name || !wizardData.new_client_name.trim()) return;
      if (!wizardData.new_client_email || !wizardData.new_client_email.trim()) return;
      const data = await createUserMutation.mutateAsync({
        nick: wizardData.new_client_name,
        email: wizardData.new_client_email.trim(),
        password: wizardData.new_client_password || generateSecurePassword(),
        sendCredentialsEmail: true,
      });
      if (data.success) {
        setWizardData(prev => ({ ...prev, client_id: data.user.id, cliente_input: data.user.nick }));
        setWizardStep(2);
      } else {
        toast.error(data.message || translate('wizard.createUserError'));
      }
    } catch {
      toast.error(translate('wizard.createUserError'));
    }
  };

  // ── Step 2: transacción ────────────────────────────────────────────────
  const handleTransKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (wizardData.transaction.trim()) setWizardStep(3);
    }
  };

  // ── Step 3: operador ───────────────────────────────────────────────────
  const handleOperInputChange = (val) => {
    setWizardData(prev => ({ ...prev, oper_input: val }));
    if (!val.trim()) { setOperMatches([]); setOperActiveIdx(0); return; }
    setOperMatches(matchUsersByNick(val));
    setOperActiveIdx(0);
  };
  const handleWizardSubmit = async (operId) => {
    try {
      const d = await createOpMutation.mutateAsync({
        transaction: wizardData.transaction,
        client_id: wizardData.client_id,
        oper_id: operId,
        category: 'terminales',
        status: 'PROSPECTO',
        industry: wizardData.industry,
      });
      if (d.op) {
        // `useCreateOp` ya invalida `['client','ops']`, no hace falta un `fetchOps()` manual.
        await createNotificationMutation.mutateAsync({
          users: [{ userId: operId, read: false }],
          user_actor: user?.id || 1,
          type: 'cliente',
          metadata: { text: 'se le ha añadido un cliente' },
          priority: 'normal',
          link: `${spacePrefix}/database/clientes`,
          space_id: user?.currentSpace?.id,
        });
      }
    } catch (err) {
      console.error('[ClientsView] Wizard submit error:', err);
    } finally {
      setWizardActive(false);
    }
  };
  const selectOper = async (selectedUser) => {
    setWizardData(prev => ({ ...prev, oper_id: selectedUser.id, oper_input: selectedUser.nick }));
    setOperMatches([]);
    await handleWizardSubmit(selectedUser.id);
  };
  const handleOperKeyDown = async (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); cycle(setOperActiveIdx, operMatches.length, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cycle(setOperActiveIdx, operMatches.length, -1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (operMatches.length > 0) await selectOper(operMatches[operActiveIdx]);
      else if (allUsers.length > 0) await selectOper(allUsers[0]);
    }
  };

  return {
    wizardActive, wizardStep, wizardData, setWizardData, setWizardStep,
    startWizard, cancelWizard,
    clientMatches, clientActiveIdx,
    industryMatches, industryActiveIdx,
    operMatches, operActiveIdx,
    clientInputRef, industryInputRef, transInputRef, operInputRef,
    handleIndustryInputChange, handleIndustryKeyDown, selectIndustry,
    handleClientInputChange, handleClientKeyDown, selectClient, startCreateClient, handleCreateClient,
    handleTransKeyDown,
    handleOperInputChange, handleOperKeyDown, selectOper,
  };
}
